import { useState, useMemo } from 'react';
import { Button, Card, Flex, Stack, useTheme, spacing } from '@fti/design-system';
import type { Publication } from '../../../types/media-sim';

interface PublicationSelectorProps {
  publications: Publication[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  loading?: boolean;
  onRefresh?: () => void;
}

export function PublicationSelector({
  publications,
  selectedIds,
  onChange,
  loading,
  onRefresh,
}: PublicationSelectorProps) {
  const { theme } = useTheme();
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search) return publications;
    const q = search.toLowerCase();
    return publications.filter((p) => p.publisher.toLowerCase().includes(q));
  }, [publications, search]);

  const togglePublication = (id: string) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((sid) => sid !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  return (
    <Card variant="outlined">
      <Card.Body>
        <Stack gap="sm">
          <Flex justify="between" align="center">
            <span style={{ fontSize: '0.85rem', color: theme.colors.text.secondary }}>
              {selectedIds.length} of {publications.length} selected
            </span>
            {onRefresh && (
              <Button variant="ghost" size="sm" onClick={onRefresh} disabled={loading}>
                Refresh
              </Button>
            )}
          </Flex>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search publications..."
            style={{
              width: '100%',
              padding: `${spacing.xs} ${spacing.sm}`,
              border: `1px solid ${theme.colors.border.default}`,
              borderRadius: '4px',
              backgroundColor: theme.colors.background.paper,
              color: theme.colors.text.primary,
              fontSize: '0.9rem',
            }}
          />
        </Stack>
        {loading ? (
          <p style={{ color: theme.colors.text.secondary, fontSize: '0.9rem' }}>
            Loading publications...
          </p>
        ) : publications.length === 0 ? (
          <p style={{ color: theme.colors.text.secondary, fontSize: '0.9rem' }}>
            No publications available. Refresh to load publications.
          </p>
        ) : (
          <div style={{ maxHeight: '300px', overflowY: 'auto', marginTop: spacing.sm }}>
            {filtered.map((pub) => (
              <Flex
                key={pub.id}
                align="center"
                gap="sm"
                style={{
                  padding: `${spacing.xs} ${spacing.sm}`,
                  borderBottom: `1px solid ${theme.colors.border.default}`,
                  cursor: 'pointer',
                }}
                onClick={() => togglePublication(pub.id)}
              >
                <input
                  type="checkbox"
                  checked={selectedIds.includes(pub.id)}
                  onChange={() => togglePublication(pub.id)}
                  style={{ cursor: 'pointer' }}
                />
                <span style={{ fontSize: '0.9rem', color: theme.colors.text.primary }}>
                  {pub.publisher}
                </span>
                <span style={{ fontSize: '0.75rem', color: theme.colors.text.secondary }}>
                  {pub.milvus_id}
                </span>
              </Flex>
            ))}
          </div>
        )}
      </Card.Body>
    </Card>
  );
}
